import axios, { STRAPI_URL } from '@propertypal/shared/src/services/axios.strapi';
import { IFaq, IFaqSubSection } from '@propertypal/shared/src/types/strapi';
import { StrapiResponse } from '@propertypal/shared/src/types/strapi/generic';
import platform from '@propertypal/shared/src/utils/platform';
import FaqPageContainer from '@propertypal/web-ui/src/faq/FaqPageContainer';
import FaqSectionContent from '@propertypal/web-ui/src/faq/FaqSectionContent';
import { GetServerSideProps } from 'next';
import Head from 'next/head';
import React, { FunctionComponent } from 'react';
import generateMetaTags from '../../../../components/layout/MetaTags';

interface FaqProps {
  data: IFaq[] | null;
  subsection?: string | null;
  subSectionName: string;
  subSectionSubtitle: string | null;
}

export const getServerSideProps: GetServerSideProps<FaqProps> = async ({ query }) => {
  const platformFilter: Record<string, string> = {
    'filters[$or][0][platform][$eq]': platform.title === 'Propertynews' ? 'Propertynews' : 'PropertyPal',
    'filters[$or][1][platform][$eq]': 'Both',
  };

  if (platform.title === 'PropertyPal') platformFilter['filters[$or][2][platform][$null]'] = 'true';

  const { section } = query;

  // get id of subsection slug
  const subSection = await axios<StrapiResponse<IFaqSubSection[]>>({
    method: 'GET',
    url: `${STRAPI_URL}/faq-sub-sections`,
    params: { populate: 'deep,3', 'filters[slug][$eq]': section, ...platformFilter },
  });

  const s = subSection.data.data?.[0];
  const sectionId = s?.id;

  // handle possibility that sub section does not exist with provided section
  if (sectionId == null)
    return {
      props: {
        data: null,
        subsection: section as string,
        subSectionName: s?.attributes?.subSection ?? 'FAQs',
        subSectionSubtitle: null,
      },
    };

  const result = await axios<StrapiResponse<IFaq[]>>({
    method: 'GET',
    url: `${STRAPI_URL}/faqs`,
    params: {
      populate: 'deep,3',
      // only get faqs with a sub section with id from section query
      'filters[subSection][id][$in]': sectionId,
      sort: 'question:asc',
    },
  });

  return {
    props: {
      data: result.data.data,
      subsection: section as string,
      subSectionName: s?.attributes?.subSection ?? 'FAQs',
      subSectionSubtitle: s?.attributes?.subtitle,
      disableHeader: true,
      disableFooter: true,
    },
  };
};

const FaqSection: FunctionComponent<FaqProps> = ({ data, subsection, subSectionName, subSectionSubtitle }) => {
  return (
    <>
      <Head>
        {generateMetaTags({
          title: `${subSectionName} - PropertyPal`,
          url: `/faq/agent-faq/${subsection}`,
        })}
      </Head>
      <FaqPageContainer
        breadcrumbs={[
          { title: 'Home', url: '/' },
          { title: 'Agent FAQ', url: '/faq/agent-faq' },
          { title: subSectionName },
        ]}
        agent
      >
        <FaqSectionContent
          data={data}
          subSectionName={subSectionName}
          subSectionSubtitle={subSectionSubtitle}
          subsection={subsection}
          agent
        />
      </FaqPageContainer>
    </>
  );
};

export default FaqSection;
